import { useRef, useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import type { PDFDocumentProxy, TextItem, PageViewport } from 'pdfjs-dist/types/src/display/api';
import type { PageCallback, OnDocumentLoadSuccess } from 'react-pdf/dist/shared/types';
import { Download, ZoomIn, ZoomOut, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfViewer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Anchor-based highlight state — reset whenever the search chunk changes.
//   phase 0: scanning for the start anchor (first ~5 words of the chunk)
//   phase 1: highlighting everything; scanning for the end anchor (last ~5 words)
//   phase 2: done — chunk fully found, stop marking
// sIdx / eIdx track progress through start / end anchors across text items.
const ANCHOR_WORDS = 5;
const MIN_COMMIT = 4; // words that must match before committing to a phase transition

// Normalize Unicode typographic variants so PDF text (curly quotes, en/em dashes)
// matches source text that may have straight ASCII equivalents.
function normalizeWord(w: string): string {
    return w
        .replaceAll(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")   // curly/prime → straight apostrophe
        .replaceAll(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')   // curly double quotes → straight
        .replaceAll(/[\u2013\u2014\u2015]/g, '-')               // en-dash, em-dash → hyphen
        .replaceAll('\u00AD', '')                               // soft hyphen → nothing
        .toLowerCase();
}

export interface PdfCitation {
    sourceText?: string;
}

interface Props {
    pdfUrl: string;
    initialPage?: number;
    citation?: PdfCitation;
    fileName: string;
    displayAllPages?: boolean;
}

interface HighlightRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface CharMapEntry {
    itemIndex: number;
}

// ─── Text search utilities ────────────────────────────────────────────────────

const LIGATURES: Record<string, string> = {
    '\uFB00': 'ff', '\uFB01': 'fi', '\uFB02': 'fl',
    '\uFB03': 'ffi', '\uFB04': 'ffl', '\uFB05': 'st', '\uFB06': 'st',
};

/**
 * Normalize text for fuzzy comparison: strip HTML, expand ligatures, strip
 * diacritics via NFD, lowercase, replace punctuation with spaces, collapse whitespace.
 */
function normalizeForSearch(text: string): string {
    return text
        .replace(/<[^>]*>/g, ' ')
        .replace(/[\uFB00-\uFB06]/g, ch => LIGATURES[ch] ?? ch)
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Concatenate all TextItem strings into one flat string and build a
 * per-character map back to the originating item index.
 */
function buildCharMap(items: TextItem[]): { fullText: string; charMap: CharMapEntry[] } {
    let fullText = '';
    const charMap: CharMapEntry[] = [];
    for (let i = 0; i < items.length; i++) {
        const str = items[i].str;
        for (let c = 0; c < str.length; c++) {
            charMap.push({ itemIndex: i });
        }
        fullText += str;
        if (items[i].hasEOL) {
            fullText += ' ';
            charMap.push({ itemIndex: i });
        }
    }
    return { fullText, charMap };
}

/**
 * Build a parallel normToOrig map: normToOrig[normPos] = origPos.
 * This lets us map a match found in normalized text back to original char positions.
 */
function buildNormToOrigMap(original: string): { normalized: string; normToOrig: number[] } {
    // Step 1: strip HTML tags, tracking orig positions
    let stripped = '';
    const strippedToOrig: number[] = [];
    let i = 0;
    while (i < original.length) {
        if (original[i] === '<') {
            const end = original.indexOf('>', i);
            if (end !== -1) {
                stripped += ' ';
                strippedToOrig.push(i);
                i = end + 1;
                continue;
            }
        }
        stripped += original[i];
        strippedToOrig.push(i);
        i++;
    }

    // Step 1b: expand ligatures + NFD decompose (strip diacritics), tracking orig positions
    let expanded = '';
    const expandedToOrig: number[] = [];
    for (let j = 0; j < stripped.length; j++) {
        const lig = LIGATURES[stripped[j]];
        if (lig) {
            for (const ch of lig) {
                expanded += ch;
                expandedToOrig.push(strippedToOrig[j]);
            }
        } else {
            const nfd = stripped[j].normalize('NFD');
            for (let k = 0; k < nfd.length; k++) {
                const cp = nfd.codePointAt(k)!;
                if (cp >= 0x0300 && cp <= 0x036f) continue; // skip combining diacritics
                expanded += nfd[k];
                expandedToOrig.push(strippedToOrig[j]);
            }
        }
    }

    // Step 2: lowercase + replace non-alnum with space, track positions
    let normalized = '';
    const normToOrig: number[] = [];
    for (let j = 0; j < expanded.length; j++) {
        const ch = expanded[j].toLowerCase();
        if (/[a-z0-9]/.test(ch)) {
            normalized += ch;
            normToOrig.push(expandedToOrig[j]);
        } else {
            if (normalized.length > 0 && normalized[normalized.length - 1] !== ' ') {
                normalized += ' ';
                normToOrig.push(expandedToOrig[j]);
            }
        }
    }
    normalized = normalized.trim();

    return { normalized, normToOrig };
}

/**
 * Search for searchText inside fullText using normalized comparison.
 * Returns { start, end } character positions in the *original* fullText,
 * or null if not found.
 */
function findMatchInNormalizedText(
    fullText: string,
    searchText: string,
): { start: number; end: number } | null {
    const { normalized: normFull, normToOrig } = buildNormToOrigMap(fullText);
    const normSearch = normalizeForSearch(searchText);
    if (!normSearch) return null;

    const idx = normFull.indexOf(normSearch);
    if (idx === -1) return null;

    const origStart = normToOrig[idx];
    const origEnd = normToOrig[Math.min(idx + normSearch.length - 1, normToOrig.length - 1)] + 1;
    return { start: origStart, end: origEnd };
}

/**
 * Convert matched character range to pixel bounding rects using the PDF viewport.
 * Returns one rect per unique TextItem that overlaps the match range.
 */
function computeHighlightRects(
    items: TextItem[],
    charMap: CharMapEntry[],
    matchRange: { start: number; end: number },
    viewport: PageViewport,
): HighlightRect[] {
    const matchedIndices = new Set<number>();
    for (let pos = matchRange.start; pos < matchRange.end && pos < charMap.length; pos++) {
        matchedIndices.add(charMap[pos].itemIndex);
    }

    const rects: HighlightRect[] = [];
    for (const idx of matchedIndices) {
        const item = items[idx];
        if (item.width === 0) continue;

        const [x, y] = viewport.convertToViewportPoint(item.transform[4], item.transform[5]);
        // height: prefer item.height; fall back to font size from transform matrix
        const itemHeight = item.height > 0
            ? item.height * viewport.scale
            : Math.abs(item.transform[3]) * viewport.scale;
        const itemWidth = item.width * viewport.scale;

        rects.push({
            left: x,
            top: y - itemHeight,
            width: itemWidth,
            height: itemHeight,
        });
    }
    return rects;
}

/**
 * Convert an explicit list of item indices to pixel bounding rects.
 * Used by the token-overlap fallback path.
 */
function computeHighlightRectsForItems(
    items: TextItem[],
    itemIndices: number[],
    viewport: PageViewport,
): HighlightRect[] {
    const rects: HighlightRect[] = [];
    for (const idx of itemIndices) {
        const item = items[idx];
        if (!item || item.width === 0) continue;
        const [x, y] = viewport.convertToViewportPoint(item.transform[4], item.transform[5]);
        const itemHeight = item.height > 0
            ? item.height * viewport.scale
            : Math.abs(item.transform[3]) * viewport.scale;
        rects.push({ left: x, top: y - itemHeight, width: item.width * viewport.scale, height: itemHeight });
    }
    return rects;
}

function tokenize(normalized: string): string[] {
    return normalized.split(' ').filter(t => t.length > 0);
}

/**
 * Fuzzy fallback: slide a window of searchTokens.length over the page's tokens
 * and return the item indices of the window with highest token overlap.
 * Returns null if best score < threshold.
 */
function findByTokenOverlap(
    items: TextItem[],
    searchText: string,
    threshold = 0.40,
): number[] | null {
    const searchTokens = tokenize(normalizeForSearch(searchText));
    if (searchTokens.length === 0) return null;
    const searchSet = new Set(searchTokens);

    const allTokens: { token: string; itemIndex: number }[] = [];
    for (let i = 0; i < items.length; i++) {
        for (const t of tokenize(normalizeForSearch(items[i].str))) {
            allTokens.push({ token: t, itemIndex: i });
        }
    }

    const W = searchTokens.length;
    if (allTokens.length < W) return null;

    let bestScore = 0;
    let bestStart = 0;
    let bestEnd = W;

    for (let w = 0; w <= allTokens.length - W; w++) {
        let hits = 0;
        for (let k = 0; k < W; k++) {
            if (searchSet.has(allTokens[w + k].token)) hits++;
        }
        const score = hits / W;
        if (score > bestScore) { bestScore = score; bestStart = w; bestEnd = w + W; }
    }

    if (bestScore < threshold) return null;
    return [...new Set(allTokens.slice(bestStart, bestEnd).map(t => t.itemIndex))];
}

// ─── Component ───────────────────────────────────────────────────────────────

const PdfViewer = ({ pdfUrl, initialPage = 1, citation, fileName, displayAllPages = false }: Props) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState(initialPage);
    const [zoom, setZoom] = useState(1);
    const [invalid, setInvalid] = useState(false);
    const [highlightRects, setHighlightRects] = useState<HighlightRect[]>([]);

    const containerRef = useRef<HTMLDivElement>(null);
    const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
    const [baseWidth, setBaseWidth] = useState<number>();
    // Per-component highlight state
    const phaseRef = useRef(0);
    const sIdxRef = useRef(0);
    const eIdxRef = useRef(0);
    // Tracks the last itemIndex seen so we can detect a new batch (e.g. React
    // StrictMode double-invoking the TextLayer effect after the first run already
    // advanced phaseRef to 2, which would suppress all highlights on the re-run).
    const lastItemIndexRef = useRef(-1);
    // Accumulated prefix of a word that is split across multiple PDF text items
    // due to ligature rendering (e.g. "su" + "ffi" + "cient" → "sufficient").
    const sPrefixRef = useRef('');
    const ePrefixRef = useRef('');

    useLayoutEffect(() => {
        const container = containerRef.current;
        const target = container?.parentElement ?? null;
        if (!target) return;
        const update = () => setBaseWidth(target.clientWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(target);
        return () => ro.disconnect();
    }, []);

    const onDocumentLoadSuccess: OnDocumentLoadSuccess = useCallback((pdf) => {
        setNumPages(pdf.numPages);
        pdfDocRef.current = pdf as unknown as PDFDocumentProxy;
    }, []);

    const handlePageRenderSuccess = useCallback(async (page: PageCallback) => {
        if (!pdfDocRef.current || !citation?.sourceText) {
            setHighlightRects([]);
            return;
        }
        try {
            const pdfPage = await pdfDocRef.current.getPage(pageNumber);
            const baseViewport = pdfPage.getViewport({ scale: 1 });
            const scale = page.width / baseViewport.width;
            const viewport = pdfPage.getViewport({ scale });
            const textContent = await pdfPage.getTextContent();
            const items = textContent.items.filter((item): item is TextItem => 'str' in item);
            const { fullText, charMap } = buildCharMap(items);
            const match = findMatchInNormalizedText(fullText, citation.sourceText);
            if (match) {
                setHighlightRects(computeHighlightRects(items, charMap, match, viewport));
            } else {
                const fuzzyIndices = findByTokenOverlap(items, citation.sourceText);
                setHighlightRects(fuzzyIndices
                    ? computeHighlightRectsForItems(items, fuzzyIndices, viewport)
                    : []);
            }
        } catch {
            setHighlightRects([]);
        }
    }, [pageNumber, citation?.sourceText]);

    // Re-compute highlights when the citation sourceText changes.
    // The onRenderSuccess callback only fires on actual PDF page renders;
    // when only the citation changes (same page), we must manually recompute.
    useEffect(() => {
        if (!citation?.sourceText || !pdfDocRef.current || !baseWidth) {
            setHighlightRects([]);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const pdfPage = await pdfDocRef.current!.getPage(pageNumber);
                const baseViewport = pdfPage.getViewport({ scale: 1 });
                const currentWidth = Math.round(baseWidth * zoom);
                const scale = currentWidth / baseViewport.width;
                const viewport = pdfPage.getViewport({ scale });
                const textContent = await pdfPage.getTextContent();
                const items = textContent.items.filter((item): item is TextItem => 'str' in item);
                const { fullText, charMap } = buildCharMap(items);
                const match = findMatchInNormalizedText(fullText, citation.sourceText!);
                if (cancelled) return;
                if (match) {
                    setHighlightRects(computeHighlightRects(items, charMap, match, viewport));
                } else {
                    const fuzzyIndices = findByTokenOverlap(items, citation.sourceText!);
                    setHighlightRects(fuzzyIndices
                        ? computeHighlightRectsForItems(items, fuzzyIndices, viewport)
                        : []);
                }
            } catch {
                if (!cancelled) setHighlightRects([]);
            }
        })();

        return () => { cancelled = true; };
    }, [citation?.sourceText, pageNumber, baseWidth, zoom]);

    // Auto-scroll to first highlight
    useEffect(() => {
        if (highlightRects.length === 0 || !containerRef.current) return;
        containerRef.current.scrollTo({ top: highlightRects[0].top - 60, behavior: 'smooth' });
    }, [highlightRects]);

    useEffect(() => {
        setPageNumber(initialPage);
    }, [initialPage]);

    useEffect(() => {
        setInvalid(pageNumber < 1 || (numPages !== 0 && pageNumber > numPages));
    }, [pageNumber, numPages]);

    const downloadPDF = () => {
        fetch(pdfUrl)
            .then(r => r.blob())
            .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.setAttribute('download', fileName);
                document.body.appendChild(a);
                a.click();
                a.parentNode?.removeChild(a);
                URL.revokeObjectURL(url);
            })
            .catch(() => { });
    };

    const renderWidth = baseWidth ? Math.round(baseWidth * zoom) : undefined;

    return (
        <>
            <div
                style={{ height: 'auto', fontSize: '1rem', backgroundColor: '#f6f3f4' }}
                className="flex flex-wrap items-center justify-between px-3 gap-2 py-2"
            >
                <div className="flex text-sm gap-2">
                    <button
                        type="button"
                        onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                        className="p-0.5 hover:opacity-75"
                    >
                        <ChevronLeft size={16} />
                    </button>

                    {numPages > 0 && (
                        <div className='flex text-sm gap-2'>
                            Page <input
                                style={{ width: '2rem' }}
                                className="pdf-viewer-input-box text-center"
                                value={`${pageNumber}`}
                                onChange={e => setPageNumber(Number(e.target.value))}
                            /> of {numPages}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setPageNumber(p => numPages ? Math.min(numPages, p + 1) : p)}
                        className="p-0.5 hover:opacity-75"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>

                <div className="flex items-center gap-1">
                    <button type="button" title="Zoom Out" onClick={() => setZoom(prev => Math.max(0.5, Number((prev - 0.1).toFixed(2))))} className="p-1 hover:opacity-75">
                        <ZoomOut size={16} />
                    </button>
                    <button type="button" title="Reset Zoom" onClick={() => setZoom(1)} className="p-1 hover:opacity-75">
                        <RotateCcw size={16} />
                    </button>
                    <button type="button" title="Zoom In" onClick={() => setZoom(prev => Math.min(4, Number((prev + 0.1).toFixed(2))))} className="p-1 hover:opacity-75">
                        <ZoomIn size={16} />
                    </button>
                </div>

                <div className="flex items-center gap-1">
                    <button type="button" onClick={downloadPDF} className="p-1 hover:opacity-75">
                        <Download size={16} />
                    </button>
                </div>
            </div>

            <div ref={containerRef} style={{ overflow: 'auto', height: 'calc(100vh - 236px)' }}>
                <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
                    {displayAllPages && renderWidth && Array.from({ length: numPages }, (_, index) => {
                        const pg = index + 1;
                        const isTargetPage = pg === initialPage;
                        return (
                            <div key={`page_${pg}`} style={{ position: 'relative' }}>
                                <Page
                                    pageNumber={pg}
                                    width={renderWidth}
                                    onRenderSuccess={isTargetPage ? handlePageRenderSuccess : undefined}
                                />
                                {isTargetPage && highlightRects.map((rect, i) => (
                                    <div key={i} className="pdf-highlight-overlay" style={{
                                        left: rect.left, top: rect.top,
                                        width: rect.width, height: rect.height,
                                    }} />
                                ))}
                            </div>
                        );
                    })}

                    {!displayAllPages && !invalid && renderWidth && (
                        <div style={{ position: 'relative' }}>
                            <Page
                                key={`page_${pageNumber}`}
                                pageNumber={pageNumber}
                                width={renderWidth}
                                onRenderSuccess={handlePageRenderSuccess}
                            />
                            {highlightRects.map((rect, i) => (
                                <div key={i} className="pdf-highlight-overlay" style={{
                                    left: rect.left, top: rect.top,
                                    width: rect.width, height: rect.height,
                                }} />
                            ))}
                        </div>
                    )}

                    {invalid && <span className="p-4 text-sm text-muted-foreground">Page could not be loaded</span>}
                    {!renderWidth && <span className="p-4 text-sm text-muted-foreground">Loading...</span>}
                </Document>
            </div>
        </>
    );
};

export default PdfViewer;
